import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { StatusPill } from "./lux";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

/**
 * Flip through every booking sitting on one vehicle/day (double-books).
 * Shared by the two availability grids — /availability and /calendar's
 * Availability tab. Lifted out of AvailabilityPage for SALES-011, where the
 * second grid was opening only one of the bookings it had just listed.
 *
 * `picker` is `{ vehicle, date, hits }`, each hit FLAT:
 *   { id, status, type, out, ret, customer, agent, from, to,
 *     category?, driver? }
 * `category` and `driver` are optional: /calendar's segments carry the raw
 * booking so it can supply them, /availability's cannot, and both render
 * correctly either way.
 */
export default function DayBookingsDialog({ picker, onClose, onOpenBooking }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [picker]);
  if (!picker) return null;

  const { vehicle, date, hits } = picker;
  const b = hits[Math.min(idx, hits.length - 1)];
  // Takes the ENTRY, not a type string: the insurance label needs the category
  // alongside the type, and it is absent on /availability's segments.
  const typeLabel = (h) => (h.type === "chauffeur" ? "Chauffeur"
    : h.type === "tour" ? "Tour"
    : h.category === "insurance" ? "Insurance replacement" : "Rental");
  const fmt = (s) => (s ? new Date(s).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="day-bookings-dialog">
        <DialogHeader>
          <DialogTitle>{vehicle.make} {vehicle.model} · {date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</DialogTitle>
          <DialogDescription>
            {hits.length} bookings share this day — switch between them below.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1.5 flex-wrap" data-testid="day-bookings-switcher">
          {hits.map((h, i) => (
            <button key={h.id || i} onClick={() => setIdx(i)} data-testid={`day-booking-tab-${i}`}
              className={`px-3 py-1.5 rounded-full border text-xs transition-colors ${
                i === idx ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
              }`}>
              {i + 1}. {typeLabel(h)}{h.customer ? ` — ${h.customer}` : ""}
            </button>
          ))}
        </div>

        <div className="rounded-2xl border border-border p-4 space-y-2.5" data-testid="day-booking-detail">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="font-heading text-lg" data-testid="day-booking-customer">{b.customer || "Client on file"}</div>
            <StatusPill status={b.status} testid="day-booking-status" />
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
            <Row label="Type" value={typeLabel(b)} testid="day-booking-type" />
            <Row label="Booked by" value={b.agent || "—"} />
            <Row label="Out" value={fmt(b.out)} testid="day-booking-out" />
            <Row label="Returns" value={fmt(b.ret)} testid="day-booking-return" />
            <Row label="From" value={b.from || "—"} />
            <Row label="To" value={b.to || "—"} />
            {/* Only when there is one — an unconditional row would put an empty
                "Driver" into /availability, whose segments never carry it. */}
            {b.driver ? <Row label="Driver" value={b.driver} testid="day-booking-driver" /> : null}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-full" disabled={idx === 0} onClick={() => setIdx(idx - 1)} data-testid="day-booking-prev">
              <CaretLeft size={13} className="mr-1" /> Previous
            </Button>
            <Button variant="outline" size="sm" className="rounded-full" disabled={idx >= hits.length - 1} onClick={() => setIdx(idx + 1)} data-testid="day-booking-next">
              Next <CaretRight size={13} className="ml-1" />
            </Button>
          </div>
          <Button size="sm" className="rounded-full" disabled={!b.id} onClick={() => onOpenBooking(b.id)} data-testid="day-booking-open">
            Open full booking
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, testid }) {
  return (
    <div>
      <div className="label-mono mb-0.5">{label}</div>
      <div className="text-foreground" data-testid={testid}>{value}</div>
    </div>
  );
}
